import { BasicTransport, Transport } from '../types';
type TransportOnlyEvents = Omit<BasicTransport, 'startSendingEvents' | 'stopSendingEvents'>;
type BufferedEvent<T extends keyof TransportOnlyEvents> = {
  name: T;
  args: Parameters<BasicTransport[T]>;
};

type Options = {
  maxSize?: number;
  fullBufferMessage?: string;
  startImmediately?: boolean;
};

export const createBufferTransport = (underlyingTransportPromise: Promise<BasicTransport>, options: Options = {}): Transport => {
  const { maxSize = 50, fullBufferMessage = 'Analyticstracker is not initialized.', startImmediately = true } = options;
  let started = startImmediately;

  let buffer: BufferedEvent<keyof TransportOnlyEvents>[] = [];
  let underlyingTransport: BasicTransport | null = null;

  const delegateToUnderlying =
    <T extends keyof TransportOnlyEvents>(name: T) =>
    async (...args: Parameters<BasicTransport[T]>) => {
      if (underlyingTransport && started) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore:next-line
        return underlyingTransport[name](...args);
      }

      const event = { name, args };
      if (buffer.length >= maxSize) {
        buffer.shift();
        console.warn(fullBufferMessage);
      }
      buffer.push(event);

      return Promise.resolve();
    };

  const flushBuffer = async () => {
    for (const event of buffer) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore:next-line
      await underlyingTransport[event.name](...event.args);
    }
    buffer = [];
  };

  underlyingTransportPromise
    .then(transport => {
      underlyingTransport = transport;

      if (started) {
        return flushBuffer();
      }

      return void 0;
    })
    .catch(error => console.error(`Failed to flush buffer (${error.message})`, error));

  return {
    identify: delegateToUnderlying('identify'),
    page: delegateToUnderlying('page'),
    track: delegateToUnderlying('track'),
    // eslint-disable-next-line promise/valid-params
    awaitInitialized: () => underlyingTransportPromise.then(),
    startSendingEvents: async () => {
      started = true;
      if (underlyingTransport) {
        await flushBuffer();
      }
    },
    stopSendingEvents: () => {
      started = false;
    },
  };
};
